import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import WarningIcon from '@mui/icons-material/Warning';

import StyledWrapper from './style';

import Table from '../Table/Table';
import ItemPageTitle from '../ItemPageTitle/ItemPageTitle';
import { BulkAssignRows } from '../../pages/IndividualProgram/ManageReviews/MockData';
import AssignReviewColumns from './AssignReviewColumns';
import ReassignIcon from '../../assets/customIcons/reassignIcon';
import LinkReviewIcon from '../../assets/customIcons/linkReviewIcon';
import ReviewFormIcon from '../../assets/customIcons/reviewForm';
import TrashcanIcon from '../../assets/customIcons/trashcanIcon';
import ReviewStageIcon from '../../assets/customIcons/reviewStageIcon';
import usePaginatedCall from '../../hooks/usePaginatedCall';
import { getUniqueValues } from '../../utils/utilFunctions';
import { getLocalAuth } from '../../utils/environmentUtils';
import { useGetReviewerPoolQuery } from '../../services/endpoints/reviewEndpoints';
import {
	useReassignDeleteMutation,
	useReassignKeepMutation,
	useUnassignDeleteMutation
} from '../../services/endpoints/reviewEndpoints';
const style = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '70%',
	maxWidth: '1060px',
	bgcolor: 'background.paper',
	borderRadius: '4px',
	boxShadow:
		'0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)',
	p: 4
};

const ReassignReview = ({ openModal, closeModal, type, item, isBulk, stageID }) => {
	const accountSource = useSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const [searchParams, setSearchParams] = useSearchParams();
	const [open, setOpen] = useState(false);
	const [openTooltip, setOpenTooltip] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [subModal, setSubModal] = useState(searchParams.get('reassignModal'));
	const [chosenReviewer, setOuterReviewer] = useState();
	const [finalRows, setFinalRows] = useState(BulkAssignRows);
	const initialColumns = AssignReviewColumns(
		openTooltip,
		setOpenTooltip,
		true,
		chosenReviewer,
		setOuterReviewer,
		'Reassign Reviewer Pool'
	);

	const reviewerPoolPagination = usePaginatedCall(useGetReviewerPoolQuery, {
		stageID: stageID?.replace('esi-', ''),
		filterBaseName: 'REASSIGN_REVIEW'
	});
	const [unassignDelete] = useUnassignDeleteMutation();
	const [reassignDelete] = useReassignDeleteMutation();
	const [reassignKeep] = useReassignKeepMutation();

	const getBulkPageTitle = () => {
		let stages = Object.values(item);
		stages = getUniqueValues(stages);

		return (
			<div style={{ display: 'flex', alignItems: 'center' }}>
				<ReviewStageIcon />
				{'Bulk '}
				{type?.includes('reassign') ? 'REASSIGN' : 'UNASSIGN'}
				{' the selected evaulation stage(s): '}
				{stages?.map((e, index) => {
					return (
						<>
							{`[${e}]`}
							{index !== stages?.length - 1 && ' and '}
						</>
					);
				})}
				{' & '}
				{type?.includes('keep') ? 'KEEP CONTENT' : 'DELETE CONTENT'}
			</div>
		);
	};

	const getPageTitle = () => {
		return (
			<>
				{isBulk && 'Bulk '}
				{type?.includes('reassign') ? 'REASSIGN' : 'UNASSIGN'}
				{' and '}
				{type?.includes('keep') ? 'KEEP CONTENT' : 'DELETE CONTENT'}
				{` the content of the review(s) by ${item?.first_name || ''} ${item?.last_name || ''}`}
				{type?.includes('reassign') ? ' to other reviewer.' : '.'}
			</>
		);
	};

	const getSubmitButtonText = () => {
		return (
			<>
				{type?.includes('reassign') ? 'REASSIGN' : 'UNASSIGN'}
				{' and '}
				{type?.includes('keep') ? 'KEEP CONTENT' : 'DELETE CONTENT'}
				<div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
					{type?.includes('reassign') ? (
						<ReassignIcon isActive={chosenReviewer} color={'white'} />
					) : (
						<LinkReviewIcon isActive={true} color={'white'} />
					)}
					{type?.includes('keep') ? (
						<ReviewFormIcon isActive={chosenReviewer} color={'white'} />
					) : (
						<TrashcanIcon
							isActive={chosenReviewer || !type?.includes('reassign')}
							color={'white'}
						/>
					)}
				</div>
			</>
		);
	};

	const handleClose = () => {
		closeModal && closeModal();
		setOpen(false);
	};

	useEffect(() => {
		const newRows = finalRows?.map((e) => {
			return {
				...e,
				checked: chosenReviewer?.id === e?.id
			};
		});
		setFinalRows(newRows);
	}, [chosenReviewer]);

	useEffect(() => {
		openModal && setOpen(true);
	}, [openModal]);

	const handleSubmit = async () => {
		setIsLoading(true);

		const options = {
			stageID: stageID?.replace('esi-', ''),
			current_reviewer: item?.reviewer_id,
			new_reviewer: chosenReviewer,
			apiToken: apiToken
		};

		if (type === 'unassign & delete') {
			unassignDelete(options)
				.unwrap()
				.then(async () => {
					setSearchParams({
						updateStatus: 'success'
					});
				})
				.catch(() => {
					setSearchParams({
						updateStatus: 'failed'
					});
				});
		} else if (type === 'reassign & keep') {
			reassignKeep(options)
				.unwrap()
				.then(async () => {
					setSearchParams({
						updateStatus: 'success'
					});
				})
				.catch(() => {
					setSearchParams({
						updateStatus: 'failed'
					});
				});
		} else if (type === 'reassign & delete') {
			reassignDelete(options)
				.unwrap()
				.then(async () => {
					setSearchParams({
						updateStatus: 'success'
					});
				})
				.catch(() => {
					setSearchParams({
						updateStatus: 'failed'
					});
				});
		}
	};

	return (
		<div>
			{!isLoading && !subModal && (
				<Modal
					open={open}
					onClose={handleClose}
					aria-labelledby="modal-modal-title"
					aria-describedby="modal-modal-description"
				>
					<Box sx={style}>
						<StyledWrapper.Title>
							{isBulk ? getBulkPageTitle() : getPageTitle()}
						</StyledWrapper.Title>
						<StyledWrapper.Description>
							{!type?.includes('keep') && <WarningIcon color={'warning'} />}
							{type?.includes('keep')
								? `The review(s) content is kept`
								: 'The review(s) content will be lost. This action cannot be undone.'}
						</StyledWrapper.Description>

						{!type?.includes('unassign') && (
							<>
								<ItemPageTitle text={'Reviewers Pool'} />
								<Table
									columns={initialColumns}
									enableAutoPageSize
									rowHeight={true}
									customHeight={'50vh'}
									hideFilters
									disableSelectionOnClick
									getRowId={(cell) => cell?.reviewer_id}
									rows={reviewerPoolPagination?.rows?.results || []}
									rowCount={reviewerPoolPagination?.rowCount}
									pagination={reviewerPoolPagination?.pagination}
									pageSize={reviewerPoolPagination?.pageSize}
									paginationMode={reviewerPoolPagination?.paginationMode}
									onPageChange={(e) => reviewerPoolPagination?.onPageChange(e)}
									onPageSizeChange={(e) => reviewerPoolPagination?.onPageSizeChange(e)}
									page={reviewerPoolPagination?.page}
									loading={reviewerPoolPagination?.loading}
									sortingMode={reviewerPoolPagination?.sortingMode}
									onSortModelChange={(e) => reviewerPoolPagination?.onSortModelChange(e)}
									filterModel={reviewerPoolPagination?.filterModel}
									sortModel={reviewerPoolPagination?.sortModel}
								/>
							</>
						)}

						<StyledWrapper.ButtonContainer>
							<Button onClick={() => handleClose()} variant="outlined" size="small">
								CANCEL
							</Button>
							<Button
								disabled={!chosenReviewer && !type?.includes('unassign')}
								onClick={handleSubmit}
								variant="contained"
								size="large"
							>
								{getSubmitButtonText()}
							</Button>
						</StyledWrapper.ButtonContainer>
					</Box>
				</Modal>
			)}
		</div>
	);
};

export default ReassignReview;
